/**
 * AccountMenuV2 — the v2 account control (UI-v2 Phase 1 shell fidelity).
 *
 * RELOCATED to the sidebar footer (.sidebar-foot) per canonical spec §1c.
 * The topbar carries NO account control — only search + toggle + bell.
 *
 * Trigger (.user-chip, canonical lines 673–682 + CSS 253–260):
 *   - flex row, gap:10px, padding:7px 8px, border-radius, cursor:pointer
 *   - hover → bg-surface-card-hover
 *   - CandidateAvatar "sm" (28px circular initials face)
 *   - .user-meta: name (13px/600, truncate) over email (11.5px, muted, truncate)
 *   - .dots: chevron-down 15×15, muted (canonical path "m6 9 6 6 6-6")
 *
 * Menu opens UPWARD (bottom-full + mb-2) — trigger is pinned to rail bottom.
 *
 * All behaviors preserved verbatim from Phase 1a:
 *   - outside-click + Esc close
 *   - useMe() / useLogout() hooks
 *   - identity block (name / email / tenantSlug)
 *   - Profile → ${base}/profile
 *   - destructive Sign out (text-status-rejected-fg)
 *   - Esc to close hint
 *   - All data-testid attributes kept intact
 *
 * ThemeToggle NOT here — v2 dark toggle lives in the topbar (AppShellV2).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { CandidateAvatar } from "@/components/v2/CandidateAvatar";
import { Separator } from "@/components/ui/separator";
import { useLogout } from "@/hooks/queries/auth/useLogout";
import { useMe } from "@/hooks/queries/auth/useMe";
import { cn } from "@/lib/utils";

function joinName(first: string, last: string): string {
  return `${first ?? ""} ${last ?? ""}`.trim();
}

/** Chevron-down 15×15, canonical .dots path "m6 9 6 6 6-6". */
function ChevronDownIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      width={15}
      height={15}
      aria-hidden
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function AccountMenuV2(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  // v2 profile lives at /:tenantSlug/v2/profile (router.tsx). The account menu
  // renders INSIDE the v2 shell, so Profile must stay in the v2 tree — pointing
  // at /:tenantSlug/profile would drop the user out to the V1 profile page and
  // out of the v2 chrome entirely (same v2→v1 drop-out class as the auth AU
  // fixes). Base carries the /v2 segment so every menu destination is v2-scoped.
  const base = useTenantBase();
  const navigate = useNavigate();
  const logout = useLogout();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Outside-click close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Esc close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  const firstName = me.data?.first_name ?? "";
  const lastName = me.data?.last_name ?? "";
  const fullName = joinName(firstName, lastName);
  const email = me.data?.email ?? "";
  const tenantSlugLabel = me.data?.tenant_slug ?? tenantSlug ?? "";
  const displayLabel = fullName || email || "Account";

  return (
    <div className="relative" ref={wrapRef} data-testid="account-menu-v2">
      {/*
       * .user-chip trigger. Canonical: flex, gap:10px, padding:7px 8px,
       * border-radius, cursor:pointer, hover → surface-card-hover.
       */}
      <button
        type="button"
        data-testid="account-menu-v2-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Account & sign out"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center rounded-[var(--radius,0.5rem)] text-text-primary transition-colors hover:bg-surface-card-hover"
        style={{ gap: "10px", padding: "7px 8px" }}
      >
        {/* .avatar.sm — 28px circular brand-gradient initials */}
        <CandidateAvatar name={displayLabel} size="sm" />

        {/* .user-meta: name (13px/600) over email (11.5px/muted) */}
        <div className="min-w-0 flex-1">
          <div
            className="truncate font-semibold text-text-primary"
            style={{ fontSize: "13px" }}
          >
            {displayLabel}
          </div>
          {email ? (
            <div
              className="truncate text-text-secondary"
              style={{ fontSize: "11.5px" }}
            >
              {email}
            </div>
          ) : null}
        </div>

        {/* .dots: chevron-down 15×15, muted */}
        <span className="shrink-0 text-text-secondary">
          <ChevronDownIcon />
        </span>
      </button>

      {/* ── Dropdown — opens UPWARD (bottom-full mb-2) since trigger is rail-bottom. ── */}
      {open ? (
        <div
          role="menu"
          data-testid="account-menu-v2-menu"
          className="absolute bottom-full left-0 right-0 z-50 mb-2 overflow-hidden rounded-xl border border-border-default bg-surface-card shadow-md"
        >
          {/* Identity block */}
          <div className="flex flex-col gap-0.5 px-4 py-3">
            {fullName ? (
              <span className="truncate text-sm font-medium text-text-primary">
                {fullName}
              </span>
            ) : null}
            <span className="truncate text-xs text-text-secondary">{email}</span>
            {tenantSlugLabel ? (
              <span className="mt-1 truncate text-[10px] font-bold uppercase tracking-[0.16em] text-text-tertiary">
                {tenantSlugLabel}
              </span>
            ) : null}
          </div>
          <Separator />
          <button
            role="menuitem"
            type="button"
            data-testid="account-menu-v2-profile-link"
            onClick={() => {
              setOpen(false);
              navigate(`${base}/profile`);
            }}
            className="block w-full px-4 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-surface-card-hover hover:text-text-primary"
          >
            Profile
          </button>
          <Separator />
          <button
            role="menuitem"
            type="button"
            data-testid="account-menu-v2-signout"
            onClick={() => {
              setOpen(false);
              logout.mutate();
            }}
            className={cn(
              "block w-full px-4 py-2 text-left text-sm font-medium text-status-rejected-fg transition-colors hover:bg-surface-card-hover",
            )}
          >
            Sign out
          </button>
          <Separator />
          <div
            role="presentation"
            data-testid="account-menu-v2-kb-hint"
            className="px-4 py-2 text-[10px] text-text-tertiary"
          >
            Esc to close
          </div>
        </div>
      ) : null}
    </div>
  );
}
